import { Badge, Banner, Button, Checkbox, Popover } from '@cloudflare/kumo';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../../components/ui/PageLayout';
import { EmptyState } from '../../components/ui/EmptyState';
import { SearchField } from '../../components/ui/SearchField';
import { Glyph, ICONS } from '../../components/ui/icons';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { ToolsTabs, ToolsetLogo, type ToolsetSummary } from './toolCatalogShared';

const BANNER_KEY = 'ia-ferramentas-catalogo-banner';

type FilterKey = 'category' | 'capability' | 'author' | 'status';
const capabilities = (t: ToolsetSummary) => [t.writes ? 'Grava dados' : 'Somente leitura', t.needsCredential ? 'Exige credencial' : 'Sem credencial'];
const statuses = (t: ToolsetSummary) => [t.installed ? 'Instaladas' : 'Disponíveis'];
const values: Record<FilterKey, (t: ToolsetSummary) => string[]> = {
  category: (t) => [t.category], capability: capabilities, author: (t) => [t.author], status: statuses,
};
const LABELS: Record<FilterKey, string> = { category: 'Categoria', capability: 'Capacidades', author: 'Autor', status: 'Status' };

/** Filtro em dropdown com busca interna e a contagem de cada item, como na lista de modelos da Cloudflare */
function FilterDropdown({ label, options, selected, onChange }: { label: string; options: Array<[string, number]>; selected: string[]; onChange: (v: string[]) => void }) {
  const [q, setQ] = useState('');
  const shown = options.filter(([name]) => name.toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (name: string) => onChange(selected.includes(name) ? selected.filter((x) => x !== name) : [...selected, name]);
  return (
    <Popover.Root>
      <Popover.Trigger render={(
        <Button variant="secondary" data-testid={`filtro-${label}`}>
          {label}{selected.length ? ` · ${selected.length}` : ''}
          <Glyph icon={ICONS.CaretDown} size={12} />
        </Button>
      )} />
      <Popover.Portal>
        <Popover.Positioner side="bottom" align="start" sideOffset={6}>
          <Popover.Popup className="w-64 max-w-[calc(100vw-1rem)] p-2">
            <SearchField value={q} onChange={setQ} placeholder={`Buscar em ${label.toLowerCase()}`} ariaLabel={`Buscar em ${label}`} />
            <ul className="mt-2 max-h-64 overflow-auto">
              {shown.map(([name, count]) => (
                <li key={name}>
                  <label className="flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-kumo-tint">
                    <span className="flex items-center gap-2">
                      <Checkbox checked={selected.includes(name)} onCheckedChange={() => toggle(name)} aria-label={name} />
                      {name}
                    </span>
                    <span className="text-kumo-subtle">{count}</span>
                  </label>
                </li>
              ))}
              {!shown.length && <li className="px-2 py-3 text-sm text-kumo-subtle">Nada encontrado</li>}
            </ul>
            {selected.length > 0 && <Button variant="ghost" size="sm" className="mt-1" onClick={() => onChange([])}>Limpar</Button>}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

function CardSkeleton() {
  return (
    <div className="animate-pulse rounded-lg border border-kumo-line p-4" aria-hidden>
      <div className="flex gap-3"><div className="h-10 w-10 rounded-lg bg-kumo-tint" /><div className="flex-1 space-y-2"><div className="h-4 w-1/2 rounded bg-kumo-tint" /><div className="h-3 w-1/3 rounded bg-kumo-tint" /></div></div>
      <div className="mt-4 space-y-2"><div className="h-3 rounded bg-kumo-tint" /><div className="h-3 w-4/5 rounded bg-kumo-tint" /></div>
    </div>
  );
}

/** IA › Ferramentas › Explorar: catálogo de integrações prontas, no estilo da lista de modelos da Cloudflare */
export function AiToolsCatalogPage() {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [items, setItems] = useState<ToolsetSummary[] | null>(null);
  const [vaultReady, setVaultReady] = useState(true);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState<Record<FilterKey, string[]>>({ category: [], capability: [], author: [], status: [] });
  const [banner, setBanner] = useState(() => { try { return localStorage.getItem(BANNER_KEY) !== '1'; } catch { return true; } });

  useEffect(() => {
    let off = false;
    (async () => {
      const r = await fetchApi('/api/ai/tool-catalog');
      const j = await r.json().catch(() => ({}));
      if (off) return;
      if (!r.ok) { toast.error(j.message || 'Não foi possível carregar o catálogo'); setItems([]); return; }
      setItems(j.toolsets); setVaultReady(j.vaultReady);
    })();
    return () => { off = true; };
  }, [fetchApi, toast]);

  const dismiss = () => { setBanner(false); try { localStorage.setItem(BANNER_KEY, '1'); } catch { /* sem storage */ } };

  const matches = (t: ToolsetSummary, skip?: FilterKey) => {
    const term = q.trim().toLowerCase();
    if (term && !`${t.name} ${t.description} ${t.author} ${t.category}`.toLowerCase().includes(term)) return false;
    return (Object.keys(filters) as FilterKey[]).every((k) => k === skip || !filters[k].length || values[k](t).some((v) => filters[k].includes(v)));
  };
  const visible = useMemo(() => (items ?? []).filter((t) => matches(t)), [items, q, filters]); // eslint-disable-line react-hooks/exhaustive-deps
  const options = (k: FilterKey): Array<[string, number]> => {
    const count = new Map<string, number>();
    for (const t of (items ?? []).filter((x) => matches(x, k))) for (const v of values[k](t)) count.set(v, (count.get(v) ?? 0) + 1);
    for (const v of filters[k]) if (!count.has(v)) count.set(v, 0);
    return [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  };
  const hasFilter = !!q.trim() || Object.values(filters).some((f) => f.length);
  const copyId = async (slug: string) => {
    try { await navigator.clipboard.writeText(slug); toast.success('ID copiado'); } catch { toast.error('Não foi possível copiar'); }
  };

  return (
    <PageLayout
      title={<span className="flex items-center gap-2"><Glyph icon={ICONS.Apps} size={24} />Ferramentas</span>}
      description="Integrações prontas para os agentes consultarem e usarem. Escolha uma, informe a credencial e ligue no agente."
      actions={<Link to="/ia/agentes"><Button variant="secondary">Ir para os agentes</Button></Link>}
    >
      <ToolsTabs active="explorar" />
      {banner && (
        <div className="mb-4">
          <Banner variant="default" description={<span className="flex flex-wrap items-center justify-between gap-2">
            <span>A chamada sai do nosso servidor e a credencial fica no cofre: ela nunca chega ao modelo.</span>
            <Button variant="ghost" size="sm" onClick={dismiss}>Dispensar</Button>
          </span>} />
        </div>
      )}
      {!vaultReady && <div className="mb-4"><Banner variant="alert" description="O cofre de credenciais não está configurado neste ambiente. Integrações sem credencial funcionam normalmente." /></div>}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="min-w-[220px] flex-1"><SearchField value={q} onChange={setQ} placeholder="Buscar integrações" ariaLabel="Buscar integrações" data-testid="catalogo-busca" /></div>
        {(Object.keys(LABELS) as FilterKey[]).map((k) => (
          <FilterDropdown key={k} label={LABELS[k]} options={options(k)} selected={filters[k]} onChange={(v) => setFilters((f) => ({ ...f, [k]: v }))} />
        ))}
      </div>
      <p className="mb-4 text-sm text-kumo-subtle" data-testid="catalogo-contagem">
        {items ? `Encontramos ${visible.length} ${visible.length === 1 ? 'integração' : 'integrações'}` : 'Carregando…'}
      </p>

      {!items && <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{[0, 1, 2, 3, 4, 5].map((i) => <CardSkeleton key={i} />)}</div>}
      {items && visible.length === 0 && (
        <EmptyState
          icon={ICONS.MagnifyingGlass}
          title="Nenhuma integração encontrada"
          description={hasFilter ? 'Tente outro termo ou limpe os filtros.' : 'O catálogo está vazio.'}
          action={hasFilter ? <Button variant="secondary" onClick={() => { setQ(''); setFilters({ category: [], capability: [], author: [], status: [] }); }}>Limpar filtros</Button> : undefined}
        />
      )}
      {items && visible.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="catalogo-grade">
          {visible.map((t) => (
            <article key={t.slug} className="group relative flex flex-col rounded-lg border border-kumo-line bg-kumo-base transition-shadow hover:shadow-md" data-testid={`catalogo-${t.slug}`}>
              <Link to={`/ia/ferramentas/${t.slug}`} className="flex flex-1 flex-col gap-3 p-4" aria-label={`${t.name}: abrir detalhes`}>
                <div className="flex items-center gap-3">
                  <ToolsetLogo name={t.name} category={t.category} />
                  <div className="min-w-0">
                    <h2 className="truncate font-semibold text-kumo-default">{t.name}</h2>
                    <div className="flex items-center gap-2 text-sm text-kumo-subtle">{t.author}<Badge variant="neutral">{t.category}</Badge></div>
                  </div>
                </div>
                <p className="line-clamp-2 text-sm text-kumo-subtle">{t.description}</p>
                <div className="mt-auto flex flex-wrap gap-1.5">
                  <Badge variant="neutral">{t.toolCount} {t.toolCount === 1 ? 'ferramenta' : 'ferramentas'}</Badge>
                  {t.writes && <Badge variant="orange">Grava dados</Badge>}
                  {!t.needsCredential && <Badge variant="neutral">Sem credencial</Badge>}
                  {t.installed && <Badge variant="green">Instalada</Badge>}
                </div>
              </Link>
              <footer className="flex items-center justify-between border-t border-kumo-line px-4 py-2 text-sm">
                <a href={t.docsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-kumo-link hover:underline">Documentação <Glyph icon={ICONS.OpenNewTab} size={12} /></a>
                <button type="button" onClick={() => copyId(t.slug)} className="text-kumo-subtle hover:text-kumo-default">Copiar ID</button>
              </footer>
            </article>
          ))}
        </div>
      )}
    </PageLayout>
  );
}
